import React, { useMemo } from "react";

import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnsType } from "antd/es/table";
import Tag from "antd/es/tag";
import { useIntl } from "react-intl";

import { Company, Deal, EntityType, Group, Person } from "@mapmycustomers/shared/types/entity";
import { OutOfCadenceEntity } from "@mapmycustomers/shared/types/entity/activities/OutOfCadenceEntity";
import { Avatar } from "@mapmycustomers/ui";

import CompanyNameWithAddress from "@app/component/company/CompanyNameWithAddress";
import DealName from "@app/component/deal/DealName";
import PersonNameWithAddress from "@app/component/person/PersonNameWithAddress";

import styles from "./ListModalColumns.module.scss";
import { messages } from "./messages";

const useRecordsOverCadenceColumns = (
  onCompanyPreview: (companyId: Company["id"]) => void,
  onPersonPreview: (personId: Person["id"]) => void
): ColumnsType<OutOfCadenceEntity> => {
  const intl = useIntl();
  return useMemo(
    () => [
      {
        dataIndex: "name",
        render: (name, entity) => {
          if (entity.entity === EntityType.COMPANY) {
            return <CompanyNameWithAddress company={entity as Company} />;
          } else if (entity.entity === EntityType.PERSON) {
            return <PersonNameWithAddress person={entity as Person} />;
          } else if (entity.entity === EntityType.DEAL) {
            return (
              <DealName
                deal={entity as Deal}
                onCompanyClick={onCompanyPreview}
                onPersonClick={onPersonPreview}
              />
            );
          }
        },
        title: intl.formatMessage(messages.name),
      },
      {
        dataIndex: "cadenceInterval",
        render: (_any: any, entity) => {
          const selectedGroup = entity.groups
            ?.filter((group) => !!group.cadenceInterval)
            ?.reduce<Group | undefined>((candidate, group) => {
              if (candidate === undefined) {
                candidate = group;
              } else {
                candidate =
                  (candidate?.cadenceInterval ?? 0) > (group.cadenceInterval ?? 0)
                    ? group
                    : candidate;
              }
              return candidate;
            }, undefined);
          return (
            <Tag>
              {intl.formatMessage(messages.cadenceIntervalTag, {
                cadenceInterval: selectedGroup && selectedGroup.cadenceInterval,
                group: selectedGroup && selectedGroup.name,
              })}
            </Tag>
          );
        },
        title: intl.formatMessage(messages.cadenceInterval),
        width: "230px",
      },
      {
        dataIndex: "cadenceDaysOut",
        render: (cadenceDaysOut: number) => {
          return (
            <div className={styles.cadenceStatus}>
              <FontAwesomeIcon icon={faExclamationCircle} />
              {intl.formatMessage(messages.cadenceStatus, {
                cadenceDaysOut: cadenceDaysOut,
              })}
            </div>
          );
        },
        title: intl.formatMessage(messages.cadenceStatusTitle),
        width: "250px",
      },
      {
        dataIndex: "user",
        render: (user) => <Avatar user={user} />,
        title: intl.formatMessage(messages.owner),
      },
    ],
    [intl, onCompanyPreview, onPersonPreview]
  );
};

export default useRecordsOverCadenceColumns;
